// Listas de precios / tarifarios y convenios. CRM → Gestión de Precios → Lista de precios.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, ListaPrecio, Modal, PartnerOpt, SENTIDO, Switch, fieldCls, inv, partnersList } from './shared';

const vacio = {
  codigo: '', nombre: '', sentido: 'venta', partner_id: '', prioridad: 0,
  comision_pct: '', comision_fija: '', vigente_desde: '', vigente_hasta: '', moneda: 'USD', es_activo: true,
};

export function ListasPrecios() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: listas = [], isFetching } = useQuery<ListaPrecio[]>({
    queryKey: ['inv-listas'], queryFn: () => inv.list<ListaPrecio>('listas_precios', '?select=*&order=nombre'),
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const partnerName = (id: string | null) => partners.find((p) => p.id === id)?.razon_social ?? '— General —';

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (l?: ListaPrecio) => {
    if (l) {
      setEditId(l.id);
      setForm({
        codigo: l.codigo ?? '', nombre: l.nombre, sentido: l.sentido, partner_id: l.partner_id ?? '', prioridad: l.prioridad,
        comision_pct: l.comision_pct?.toString() ?? '', comision_fija: l.comision_fija?.toString() ?? '',
        vigente_desde: l.vigente_desde ?? '', vigente_hasta: l.vigente_hasta ?? '', moneda: l.moneda, es_activo: l.es_activo,
      });
    } else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = {
        codigo: form.codigo || null, nombre: form.nombre, sentido: form.sentido, partner_id: form.partner_id || null,
        prioridad: Number(form.prioridad) || 0,
        comision_pct: form.comision_pct === '' ? null : Number(form.comision_pct),
        comision_fija: form.comision_fija === '' ? null : Number(form.comision_fija),
        vigente_desde: form.vigente_desde || null, vigente_hasta: form.vigente_hasta || null,
        moneda: form.moneda, es_activo: form.es_activo,
      };
      return editId ? inv.update('listas_precios', editId, body) : inv.create('listas_precios', body);
    },
    onSuccess: () => { toast.success('Lista guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-listas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('listas_precios', id),
    onSuccess: () => { toast.success('Lista eliminada'); void qc.invalidateQueries({ queryKey: ['inv-listas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Listas de precios y convenios</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva lista</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Tarifarios ({listas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Sentido</th><th className="py-2 pr-3">Convenio (partner)</th>
              <th className="py-2 pr-3 text-right">Prioridad</th><th className="py-2 pr-3 text-right">Comisión</th>
              <th className="py-2 pr-3">Vigencia</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {listas.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin listas</td></tr>}
              {listas.map((l) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{l.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{l.sentido}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{partnerName(l.partner_id)}</td>
                  <td className="py-2 pr-3 text-right">{l.prioridad}</td>
                  <td className="py-2 pr-3 text-right">{l.comision_pct != null ? `${l.comision_pct}%` : l.comision_fija != null ? `$${l.comision_fija}` : '—'}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{l.vigente_desde || '—'} → {l.vigente_hasta || '∞'}</td>
                  <td className="py-2 pr-3">{l.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(l)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${l.nombre}"?`, { peligro: true })) eliminar.mutate(l.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar lista' : 'Nueva lista'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label="Sentido">
                <select className={fieldCls} value={form.sentido} onChange={(e) => set({ sentido: e.target.value })}>
                  {SENTIDO.map((s) => <option key={s} value={s}>{s === 'venta' ? 'Venta (cliente)' : 'Compra (proveedor)'}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            <Campo label="Convenio con partner (vacío = lista general)">
              <select className={fieldCls} value={form.partner_id} onChange={(e) => set({ partner_id: e.target.value })}>
                <option value="">— General —</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({(p.tipo_partner || []).join('/')})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Prioridad"><input type="number" className={fieldCls} value={form.prioridad} onChange={(e) => set({ prioridad: Number(e.target.value) })} /></Campo>
              <Campo label="Comisión %"><input type="number" step="0.001" className={fieldCls} value={form.comision_pct} onChange={(e) => set({ comision_pct: e.target.value })} /></Campo>
              <Campo label="Comisión fija"><input type="number" step="0.01" className={fieldCls} value={form.comision_fija} onChange={(e) => set({ comision_fija: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Vigente desde"><input type="date" className={fieldCls} value={form.vigente_desde} onChange={(e) => set({ vigente_desde: e.target.value })} /></Campo>
              <Campo label="Vigente hasta"><input type="date" className={fieldCls} value={form.vigente_hasta} onChange={(e) => set({ vigente_hasta: e.target.value })} /></Campo>
              <Campo label="Moneda"><input className={fieldCls} value={form.moneda} onChange={(e) => set({ moneda: e.target.value })} /></Campo>
            </div>
            <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
